/**
 * Reads a picked image into a data URL.
 *
 * The app has no multipart upload path and does not need one for these: a
 * letterhead mark and a signature are small, they belong *inside* the record
 * that uses them, and keeping them there means no second store to keep in step
 * and no orphaned blob when the record is deleted. `server/print/engine/fonts.ts`
 * also denies pdfmake both the network and the filesystem, so a data URL is the
 * one form the renderer will accept — see shared/schemas/signature.ts.
 *
 * The limits are checked here as well as on the server so the error arrives
 * before the save rather than after it, and they are passed in rather than
 * hard-coded because a 512 KB logo and a 256 KB signature are different
 * decisions.
 */

interface ReadImageOptions {
  maxBytes: number;
  accepted: readonly string[];
}

/** Thrown with a message meant for the operator, not for a log. */
export class ImageReadError extends Error {}

const label = (types: readonly string[]): string =>
  types
    .map((type) => type.replace('image/', '').toUpperCase())
    .join(', ')
    .replace(/, ([^,]*)$/, ' or $1');

export function readImageDataUrl(file: File, options: ReadImageOptions): Promise<string> {
  // Checked before the read, so an oversized file is never pulled into memory
  // just to be rejected.
  if (!options.accepted.includes(file.type)) {
    throw new ImageReadError(`Use a ${label(options.accepted)} image.`);
  }
  if (file.size > options.maxBytes) {
    throw new ImageReadError(
      `That file is ${Math.round(file.size / 1024)} KB. The limit is ${Math.round(
        options.maxBytes / 1024,
      )} KB.`,
    );
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      // `readAsDataURL` yields a string; anything else means the read did not
      // produce what we asked for, which is a failure rather than a value.
      if (typeof result === 'string') resolve(result);
      else reject(new ImageReadError('Could not read that file.'));
    };

    reader.onerror = () => reject(new ImageReadError('Could not read that file.'));
    reader.readAsDataURL(file);
  });
}
